import React, { useState, useEffect, memo } from 'react'
import TableOperationsUI from '../../../core-system/Table/TableOperationsUI'
import {
  filterRows,
  searchRows,
  sortRows,
} from '../../../core-system/Table/helperFunctions'
import { FilterCheckbox } from '../../../core-system/Table/FilterModel'
import { find } from 'lodash-es'

interface SegmentOperationsProps {
  searchArray: string[] // Keys to search in the segments
  filters: any[]
  segments: any[]
  setFilteredSegments: (segments: any[]) => void
  sortKey: string
}

const SegmentOperations = memo(
  ({
    searchArray,
    filters,
    segments,
    setFilteredSegments,
    sortKey,
  }: SegmentOperationsProps) => {
    // Local state for displayed rows and filters/sort
    const [filterSet, setFilterSet] = useState(false)
    const [searchSet, setSearchSet] = useState(false)
    const [searchManipulatedRows, setSearchManipulatedRows] = useState<any[]>(
      []
    )
    const [filterManipulatedRows, setFilterManipulatedRows] = useState<any[]>(
      []
    )
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
      null
    )
    const [searchValue, setSearchValue] = useState('')
    const [isFilterButtonActive, setIsFilterButtonActive] = useState(false)
    const [isSearchActive, setIsSearchActive] = useState(false)

    // Get the final rows to display
    // getting the common rows between search, filter, and sort
    const finalRows = () => {
      // If nothing is set, return all rows
      if (!searchSet && !filterSet && !sortDirection) {
        return segments
      }
      // If only sort is set, return sorted rows
      if (!searchSet && !filterSet && sortDirection) {
        return sortRows(segments, sortKey, sortDirection)
      }
      const commonRows = []
      // If any row is empty the result will be empty
      if (searchSet && searchManipulatedRows.length === 0) {
        return []
      }
      if (filterSet && filterManipulatedRows.length === 0) {
        return []
      }
      if (searchManipulatedRows.length)
        searchManipulatedRows.forEach((row) => {
          if (filterManipulatedRows.length) {
            if (
              find(filterManipulatedRows, { id: row.id }) &&
              !find(commonRows, { id: row.id })
            )
              commonRows.push(row)
          } else {
            commonRows.push(row)
          }
        })
      if (filterManipulatedRows.length)
        filterManipulatedRows.forEach((row) => {
          if (searchManipulatedRows.length) {
            if (
              find(searchManipulatedRows, { id: row.id }) &&
              !find(commonRows, { id: row.id })
            )
              commonRows.push(row)
          } else {
            commonRows.push(row)
          }
        })
      return sortRows(commonRows, sortKey, sortDirection)
    }

    // Push up the current rows whenever they change
    useEffect(() => {
      setFilteredSegments(finalRows())
    }, [searchManipulatedRows, filterManipulatedRows, sortDirection])

    // Handlers
    const handleApplyFilters = (filters: FilterCheckbox[]) => {
      const filtered = filterRows(segments, filters)
      setFilterManipulatedRows(filtered)
      setFilterSet(true)
      setIsFilterButtonActive(true)
    }

    const handleFiltersReset = () => {
      setFilterManipulatedRows([])
      setFilterSet(false)
      setIsFilterButtonActive(false)
    }

    const handleSearch = (value: string) => {
      if (value) {
        const searched = searchRows(segments, searchArray, value)
        setSearchManipulatedRows(searched)
        setSearchSet(true)
        setSearchValue(value)
        setIsSearchActive(true)
      } else {
        setSearchManipulatedRows([])
        setSearchSet(false)
        setSearchValue('')
        setIsSearchActive(false)
      }
    }

    const handleSearchClear = () => {
      setSearchManipulatedRows([])
      setSearchSet(false)
      setSearchValue('')
      setIsSearchActive(false)
    }

    const handleSortAsc = () => {
      setSortDirection('asc')
    }

    const handleSortDesc = () => {
      setSortDirection('desc')
    }

    return (
      <TableOperationsUI
        handleSearch={handleSearch}
        handleSearchClear={handleSearchClear}
        onAscendingClick={handleSortAsc}
        onDescendingClick={handleSortDesc}
        filters={filters}
        handleApplyFilters={handleApplyFilters}
        handleFiltersReset={handleFiltersReset}
        isFilterButtonActive={isFilterButtonActive}
        activeSortDirection={sortDirection}
        isSearchActive={isSearchActive}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
      />
    )
  }
)

export default SegmentOperations
